/* eslint-disable react-hooks/exhaustive-deps */
import AddButtonRounded from '@Components/AddButtonRounded';
import Flex from '@Components/Bases/Flex';
import DeleteModal from '@Components/Modal/DeleteModal';
import { useUploadImage } from '@Domains/UploadImage';
import { useDisclosure } from '@hooks/useDisclosure';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import { useGreenhouseForm } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider';
import { useImpactsOnStakeholdersForm } from '@Router/ImpactsOnStakeholdersManagement/Containers/ImpactsOnStakeholdersFormProvider';
import { useNatureOfBusinessForm } from '@Router/NatureOfBusiness/Containers/NatureOfBusinessFormProvider';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { defaultEnAssetState, defaultThAssetState } from './defaultAssetStates';
import ImageFields from './ImageComponent/ImageFields';
import SnackbarErrorUploadImage from './ImageComponent/SnackbarErrorUploadImage';
import UploadField from './ImageComponent/UploadField';
import UseSameFileCheckbox from './ImageComponent/UseSameFileCheckbox';
import {
  AssetType,
  getDisplayMessage,
  getErrorMessage,
  getFileNameByUrl,
  mapdata,
} from './utils';

type UploadImageFormFieldsProps = {
  title: string;
  name: string;
  disabledTh?: boolean;
  disabledEn?: boolean;
  fieldPath?: string;
};

const UploadImageFormFields = (props: UploadImageFormFieldsProps) => {
  const { name, title, disabledTh = false, disabledEn = false } = props;
  const [opened, { open, close }] = useDisclosure();
  const { id } = useParams();
  const {
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const assets = useWatch({ name: `${name}.assets` }) ?? ([] as AssetType[]);
  const { fields, append, remove, update } = useFieldArray({
    name: `${name}.assets`,
  });
  const { uploadImage } = useUploadImage(Number(id));
  const [indexSelected, setIndexSelected] = useState<number>(0);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const { refetchProgressWithUpdate } = useProgressBar();
  const location = useLocation();
  const path = location.pathname.split('/').pop();

  const impactsOnStakeholdersFormMethods = useImpactsOnStakeholdersForm();
  const natureOfBusinessFormMethods = useNatureOfBusinessForm();
  const goalsAndOperationalPlansFormMethods = useNatureOfBusinessForm();
  const useGreenhouseFormFormMethods = useGreenhouseForm();

  const { getValues, reset, trigger } =
    path === 'impacts-on-stakeholders-management'
      ? impactsOnStakeholdersFormMethods
      : path === 'nature-of-business'
      ? natureOfBusinessFormMethods
      : path === 'goals-and-operational-plans'
      ? goalsAndOperationalPlansFormMethods
      : useGreenhouseFormFormMethods;

  const handleAddAsset = () => {
    append({
      ...defaultThAssetState,
      ...defaultEnAssetState,
      isUseSameFile: false,
    } as AssetType);
  };

  const handleOpenDisclosure = (assetIndex: number) => {
    setIndexSelected(assetIndex);
    open();
  };

  useEffect(() => {
    assets.forEach((asset: AssetType, index: number) => {
      const assetPath = `${name}.assets.${index}`;

      if (asset.isUseSameFile === true && asset.fileNameTh) {
        setValue(`${assetPath}.fileNameEn`, asset.fileNameTh);
        setValue(`${assetPath}.urlEn`, null);
      } else if (asset.isUseSameFile === false) {
        setValue(`${assetPath}.fileNameEn`, asset.fileNameEn || null);
        setValue(`${assetPath}.urlEn`, asset.urlEn || null);
      }
    });
  }, [JSON.stringify(assets), name, setValue]);

  const handleFileUpload = async (
    file: File,
    index: number,
    locale: string
  ) => {
    const fileNameKey = `${name}.assets.${index}.fileName${
      locale === 'th' ? 'Th' : 'En'
    }`;

    clearErrors(fileNameKey);

    try {
      const responseData = await uploadImage(file, index, name, locale);
      const currentFieldData = fields[index];
      const currentAsset = assets[index];
      const descriptionTh = currentAsset?.descriptionTh;
      const descriptionEn = currentAsset?.descriptionEn;
      const modifiedFieldData = {
        ...currentFieldData,
        id: currentAsset.id,
        isUseSameFile:
          currentAsset?.isUseSameFile === undefined
            ? false
            : currentAsset?.isUseSameFile,
        descriptionTh,
        descriptionEn,
      };
      const updatedData = mapdata({
        ...modifiedFieldData,
        ...responseData,
        descriptionTh,
        descriptionEn,
      });

      update(index, updatedData);

      if (assets[index]?.isUseSameFile && locale === 'th') {
        setValue(`${name}.assets.${index}.fileNameEn`, responseData.fileNameTh);
        setValue(`${name}.assets.${index}.urlEn`, null);
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.error || 'unknown-error';
      const errorMessage =
        error?.response?.data?.message || 'error file not found';
      const displayMessage = getDisplayMessage(errorCode, errorMessage);

      setError(fileNameKey, { type: 'manual' });
      setSnackbarMessage(displayMessage);
    }
  };

  const handleFileCancelAll = async (assetIndex: number) => {
    remove(assetIndex);
    reset({
      ...getValues(),
    });
    refetchProgressWithUpdate(true);
  };

  const handleFileCancel = async (index: number, locale: string) => {
    const fileNameTh = getFileNameByUrl(assets[index]?.urlTh);
    const fileNameEn = getFileNameByUrl(assets[index]?.urlEn);
    const isUseSameFile = assets[index]?.isUseSameFile;
    const updatedAssets = [...assets];

    if (fileNameTh === fileNameEn && !isUseSameFile) {
      if (locale === 'th') {
        updatedAssets[index] = {
          ...updatedAssets[index],
          ...defaultThAssetState,
          ...defaultEnAssetState,
          markedForDeletion: true,
          fileNameThForDeletion: fileNameTh,
          fileNameEnForDeletion: fileNameEn,
        };
      }
      if (locale === 'en') {
        updatedAssets[index] = {
          ...updatedAssets[index],
          ...defaultThAssetState,
          ...defaultEnAssetState,
          markedForDeletion: true,
          fileNameThForDeletion: fileNameTh,
          fileNameEnForDeletion: fileNameEn,
        };
      }

      setValue(`${name}.assets`, updatedAssets);
      return;
    }
    try {
      const fileName =
        locale === 'th'
          ? getFileNameByUrl(assets[index]?.urlTh)
          : getFileNameByUrl(assets[index]?.urlEn);

      if (fileName) {
        if (locale === 'th') {
          updatedAssets[index] = {
            ...updatedAssets[index],
            ...defaultThAssetState,
            markedForDeletion: true,
            fileNameThForDeletion: fileNameTh,
            fileNameEnForDeletion: undefined,
          };

          if (isUseSameFile) {
            updatedAssets[index] = {
              ...updatedAssets[index],
              ...defaultEnAssetState,
              isUseSameFile: false,
            };
          }
        } else {
          updatedAssets[index] = {
            ...updatedAssets[index],
            ...defaultEnAssetState,
            markedForDeletion: true,
            fileNameThForDeletion: undefined,
            fileNameEnForDeletion: fileNameEn,
          };
        }

        updatedAssets[index].isUseSameFile = false;
        setValue(`${name}.assets`, updatedAssets);
      }
    } catch (error) {
      console.error(`Error deleting ${locale} file:`, error);
    }
  };

  useEffect(() => {
    if (assets.length > 0) {
      assets.forEach((asset: AssetType, index: number) => {
        if (asset?.urlTh && asset?.urlTh !== null) {
          const image = document.getElementById(
            `${name}-th-image-${index}`
          ) as HTMLImageElement;
          if (image) {
            image.src = asset.urlTh ?? '';
          }
        }
        if (asset?.urlEn && asset?.urlEn !== null && !asset.isUseSameFile) {
          const image = document.getElementById(
            `${name}-en-image-${index}`
          ) as HTMLImageElement;
          if (image) {
            image.src = asset.urlEn ?? '';
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);

  return (
    <Box>
      <Typography fontWeight={800}>{`${title} (ถ้ามี)`}</Typography>
      {fields.map((field, index: number) => {
        const hasThaiImage =
          assets[index]?.urlTh !== null && assets[index]?.urlTh !== undefined;
        const hasEnglishImage =
          assets[index]?.urlEn !== null && assets[index]?.urlEn !== undefined;

        return (
          <Box
            p={2}
            mt={2}
            border={'1px solid hsla(240, 2%, 90%, 1)'}
            key={field.id}
          >
            <Flex justifyContent={'space-between'} alignItems={'center'}>
              <Typography fontWeight={800}>
                {`แนบรูปภาพประกอบเพิ่มเติม ที่ ${index + 1}`}
              </Typography>
              <IconButton
                size={'small'}
                onClick={() => handleOpenDisclosure(index)}
                disabled={disabledTh || disabledEn}
              >
                <DeleteOutlineOutlinedIcon fontSize={'small'} />
              </IconButton>
            </Flex>
            <UploadField
              name={name}
              index={index}
              assets={assets}
              trigger={trigger}
              errors={errors}
              getErrorMessage={getErrorMessage}
              handleFileUpload={handleFileUpload}
              handleFileCancel={handleFileCancel}
              disabledTh={disabledTh}
              disabledEn={disabledEn}
              local={'Th'}
            />
            {hasThaiImage && (
              <ImageFields
                name={name}
                index={index}
                local={'Th'}
                disabledTh={disabledTh}
                disabledEn={disabledEn}
              />
            )}
            <UseSameFileCheckbox
              name={name}
              index={index}
              assets={assets}
              disabledTh={disabledTh}
              disabledEn={disabledEn}
            />
            <UploadField
              name={name}
              index={index}
              assets={assets}
              trigger={trigger}
              errors={errors}
              getErrorMessage={getErrorMessage}
              handleFileUpload={handleFileUpload}
              handleFileCancel={handleFileCancel}
              local={'En'}
              disabledTh={disabledTh}
              disabledEn={disabledEn}
            />
            {(hasEnglishImage || !assets[index]?.isUseSameFile) && (
              <ImageFields
                name={name}
                index={index}
                local={'En'}
                disabledTh={disabledTh}
                disabledEn={disabledEn}
              />
            )}
          </Box>
        );
      })}

      <DeleteModal
        topic={`รูปภาพประกอบเพิ่มเติม ที่ ${indexSelected + 1}`}
        opened={opened}
        close={close}
        confirm={() => {
          handleFileCancelAll(indexSelected);
          close();
        }}
      />

      <Box mt={2}>
        <AddButtonRounded
          name={`เพิ่ม${title}`}
          onClick={handleAddAsset}
          disabled={disabledTh || disabledEn}
        />
      </Box>
      <Box>
        {snackbarMessage && (
          <SnackbarErrorUploadImage
            errorMessage={snackbarMessage}
            onClose={() => setSnackbarMessage(null)}
          />
        )}
      </Box>
    </Box>
  );
};

export default UploadImageFormFields;
